import { TFunction } from "i18next";
import {
	AccentColor,
	NeutralColor,
	SecondaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import {
	AccessoriesSubMatrix,
	ChemicalConcentrateExtractSubMatrix,
	ConsumablesSubMatrix,
	DriedCannabisSubMatrix,
	FreshCannabisSubMatrix,
	FrontendTurnaroundTimeOptions,
	HempSubMatrix,
	OtherSubMatrix,
	PhysicalConcentrateExtractSubMatrix,
	InstrumentCondition,
	SalmonCustomOption,
	SampleCategory,
	SampleMatrix,
	SampleSubMatrix,
	TestPackages,
	TopicalsSubMatrix,
	TurnaroundTime,
	InstrumentType,
	InstrumentSource,
	OrderItemType,
	InstallFamiliarizeOptions,
	RepairOptions,
	TaxOptions,
	DiscountUnit,
} from "node-library/dist/constants";
import {
	FlowerTestingOptions,
	FrontendSampleTestInformation,
	MicroTestingOptions,
	OilTestingOptions,
	TestOptionFields,
	TestPackageInfo,
	BasicTestOptionTimes,
	CombinedTestingOptions,
	FrontendOrderItem,
	IQSelection,
	FrontendService,
} from "node-library/dist/portal";

import {
	isAnyMicroTestEnabled,
	isAnyMicroTestWithoutAdditionalEnabled,
} from "node-library/dist/helpers/sample_helper";
import { useTranslation } from "react-i18next";

export function getCategoryFromSubMatrix(
	subMatrix: SampleSubMatrix
): SampleCategory {
	switch (subMatrix) {
		case DriedCannabisSubMatrix.FLOWER:
			return SampleCategory.FLOWER;
		case DriedCannabisSubMatrix.INFUSED_FLOWER:
			return SampleCategory.HYBRID;
		case DriedCannabisSubMatrix.MILLED:
			return SampleCategory.FLOWER;
		case DriedCannabisSubMatrix.INFUSED_MILLED:
			return SampleCategory.HYBRID;
		case DriedCannabisSubMatrix.PRE_ROLL:
			return SampleCategory.FLOWER;
		case DriedCannabisSubMatrix.INFUSED_PRE_ROLL:
			return SampleCategory.HYBRID;
		case DriedCannabisSubMatrix.SHAKE:
			return SampleCategory.FLOWER;
		case DriedCannabisSubMatrix.TRIM:
			return SampleCategory.FLOWER;
		case HempSubMatrix.HEMP:
			return SampleCategory.FLOWER;
		case FreshCannabisSubMatrix.FLOWER:
			return SampleCategory.FLOWER;
		case FreshCannabisSubMatrix.LEAF:
			return SampleCategory.OTHER;
		case FreshCannabisSubMatrix.ROOT:
			return SampleCategory.OTHER;
		case ConsumablesSubMatrix.CANNABIS_OIL:
			return SampleCategory.OIL;
		case ConsumablesSubMatrix.SPRAYS:
			return SampleCategory.FLOWER;
		case ConsumablesSubMatrix.TINCTURES:
			return SampleCategory.FLOWER;
		case ConsumablesSubMatrix.BEVERAGE:
			return SampleCategory.OTHER;
		case ConsumablesSubMatrix.EDIBLES:
			return SampleCategory.OTHER;
		case TopicalsSubMatrix.CREAMS:
			return SampleCategory.FLOWER;
		case TopicalsSubMatrix.LINIMENTS:
			return SampleCategory.FLOWER;
		case TopicalsSubMatrix.SALVES:
			return SampleCategory.FLOWER;
		case ChemicalConcentrateExtractSubMatrix.BADDER:
		case ChemicalConcentrateExtractSubMatrix.LIVE_BADDER:
		case ChemicalConcentrateExtractSubMatrix.DIAMONDS:
		case ChemicalConcentrateExtractSubMatrix.DISTILLATE:
		case ChemicalConcentrateExtractSubMatrix.HASH_OIL:
		case ChemicalConcentrateExtractSubMatrix.ISOLATE:
		case ChemicalConcentrateExtractSubMatrix.RESIN:
		case ChemicalConcentrateExtractSubMatrix.LIVE_RESIN:
		case ChemicalConcentrateExtractSubMatrix.SHATTER:
		case ChemicalConcentrateExtractSubMatrix.WAX:
			return SampleCategory.HYBRID;
		case PhysicalConcentrateExtractSubMatrix.HASH:
		case PhysicalConcentrateExtractSubMatrix.DRY_SIFT_HASH:
		case PhysicalConcentrateExtractSubMatrix.KIEF:
		case PhysicalConcentrateExtractSubMatrix.PRESSED_KIEF:
		case PhysicalConcentrateExtractSubMatrix.ROSIN:
		case PhysicalConcentrateExtractSubMatrix.FLOWER_ROSIN:
		case PhysicalConcentrateExtractSubMatrix.HASH_ROSIN:
		case PhysicalConcentrateExtractSubMatrix.LIVE_ROSIN:
			return SampleCategory.FLOWER;
		case AccessoriesSubMatrix.ROLLING_PAPER:
		case OtherSubMatrix.SOLID:
		case OtherSubMatrix.INHALABLE:
			return SampleCategory.OTHER;
	}
}

export function canSubmatrixDoUrgent(subMatrix: SampleSubMatrix): boolean {
	switch (subMatrix) {
		case DriedCannabisSubMatrix.FLOWER:
		case DriedCannabisSubMatrix.INFUSED_FLOWER:
		case DriedCannabisSubMatrix.MILLED:
		case DriedCannabisSubMatrix.INFUSED_MILLED:
		case DriedCannabisSubMatrix.PRE_ROLL:
		case DriedCannabisSubMatrix.INFUSED_PRE_ROLL:
		case DriedCannabisSubMatrix.SHAKE:
		case DriedCannabisSubMatrix.TRIM:
		case HempSubMatrix.HEMP:
			return true;
		case FreshCannabisSubMatrix.FLOWER:
		case FreshCannabisSubMatrix.LEAF:
		case FreshCannabisSubMatrix.ROOT:
		case ConsumablesSubMatrix.CANNABIS_OIL:
		case ConsumablesSubMatrix.SPRAYS:
		case ConsumablesSubMatrix.TINCTURES:
		case ConsumablesSubMatrix.BEVERAGE:
		case ConsumablesSubMatrix.EDIBLES:
		case TopicalsSubMatrix.CREAMS:
		case TopicalsSubMatrix.LINIMENTS:
		case TopicalsSubMatrix.SALVES:
		case ChemicalConcentrateExtractSubMatrix.BADDER:
		case ChemicalConcentrateExtractSubMatrix.LIVE_BADDER:
		case ChemicalConcentrateExtractSubMatrix.DIAMONDS:
		case ChemicalConcentrateExtractSubMatrix.DISTILLATE:
		case ChemicalConcentrateExtractSubMatrix.HASH_OIL:
		case ChemicalConcentrateExtractSubMatrix.ISOLATE:
		case ChemicalConcentrateExtractSubMatrix.RESIN:
		case ChemicalConcentrateExtractSubMatrix.LIVE_RESIN:
		case ChemicalConcentrateExtractSubMatrix.SHATTER:
		case ChemicalConcentrateExtractSubMatrix.WAX:
		case PhysicalConcentrateExtractSubMatrix.HASH:
		case PhysicalConcentrateExtractSubMatrix.DRY_SIFT_HASH:
		case PhysicalConcentrateExtractSubMatrix.KIEF:
		case PhysicalConcentrateExtractSubMatrix.PRESSED_KIEF:
		case PhysicalConcentrateExtractSubMatrix.ROSIN:
		case PhysicalConcentrateExtractSubMatrix.FLOWER_ROSIN:
		case PhysicalConcentrateExtractSubMatrix.HASH_ROSIN:
		case PhysicalConcentrateExtractSubMatrix.LIVE_ROSIN:
		case AccessoriesSubMatrix.ROLLING_PAPER:
		case OtherSubMatrix.SOLID:
		case OtherSubMatrix.INHALABLE:
			return false;
	}
}
export function getSubMatrixKeysFromMatrix(
	matrix: SampleMatrix
): Array<SampleSubMatrix> {
	switch (matrix) {
		case SampleMatrix.DRIED_CANNABIS:
			return Object.values(DriedCannabisSubMatrix);
		case SampleMatrix.HEMP:
			return Object.values(HempSubMatrix);

		case SampleMatrix.FRESH_CANNABIS:
			return Object.values(FreshCannabisSubMatrix);

		case SampleMatrix.CONSUMABLES:
			return Object.values(ConsumablesSubMatrix);

		case SampleMatrix.TOPICALS:
			return Object.values(TopicalsSubMatrix);

		case SampleMatrix.CHEMICAL_CONCENTRATE_EXTRACT:
			return Object.values(ChemicalConcentrateExtractSubMatrix);

		case SampleMatrix.PHYSICAL_CONCENTRATE_EXTRACT:
			return Object.values(PhysicalConcentrateExtractSubMatrix);

		case SampleMatrix.ACCESSORIES:
			return Object.values(AccessoriesSubMatrix);

		case SampleMatrix.OTHER:
			return Object.values(OtherSubMatrix);
	}
}
export function convertSelectedBasicTestOptionsToFields(
	subMatrix: SampleSubMatrix,
	basicTestOptions: FlowerTestingOptions | OilTestingOptions | undefined,
	translation: TFunction<"translation", undefined>
): Array<TestOptionFields> {
	const basicTestOptionFields: Array<any> = [
		{
			id: "CannabinoidPotency",
			title: translation("CannabinoidPotency"),
			checked: basicTestOptions?.cannabinoid,
		},
		{
			id: "Terpene",
			title: translation("Terpene"),
			checked: basicTestOptions?.terpene,
		},
		{
			id: "Pesticides",
			title: translation("Pesticides"),
			checked: basicTestOptions?.pesticides,
		},
		{
			id: "Mycotoxins",
			title: translation("Mycotoxins"),
			checked: basicTestOptions?.mycotoxins,
		},
		{
			id: "HeavyMetal",
			title: translation("HeavyMetal"),
			checked: basicTestOptions?.heavyMetals,
		},
		{
			id: "ForeignMatter",
			title: translation("ForeignMatter"),
			checked: basicTestOptions?.foreignMatter,
		},
		{
			id: "WaterActivity",
			title: translation("WaterActivity"),
			checked: basicTestOptions?.waterActivity,
		},
	];

	const category: SampleCategory = getCategoryFromSubMatrix(subMatrix);

	if (category === SampleCategory.FLOWER) {
		basicTestOptionFields.push({
			id: "MoistureContent",
			title: translation("MoistureContent"),
			checked: (basicTestOptions as FlowerTestingOptions).moistureContent,
		});
	} else if (category === SampleCategory.OIL) {
		basicTestOptionFields.push({
			id: "ResidualSolvent",
			title: translation("ResidualSolvent"),
			checked: (basicTestOptions as OilTestingOptions).residualSolvent.enabled,
			additionalInfo:
				(basicTestOptions as OilTestingOptions).residualSolvent.propane &&
				(basicTestOptions as OilTestingOptions).residualSolvent.butane
					? "(+Propane, +Butane)"
					: (basicTestOptions as OilTestingOptions).residualSolvent.propane
					? "(+Propane)"
					: (basicTestOptions as OilTestingOptions).residualSolvent.butane
					? "(+Butane)"
					: "",
		});
	} else {
		basicTestOptionFields.push({
			id: "MoistureContent",
			title: translation("MoistureContent"),
			checked: (basicTestOptions as CombinedTestingOptions).moistureContent,
		});
		basicTestOptionFields.push({
			id: "ResidualSolvent",
			title: translation("ResidualSolvent"),
			checked: (basicTestOptions as CombinedTestingOptions).residualSolvent
				.enabled,
			additionalInfo:
				(basicTestOptions as OilTestingOptions).residualSolvent.propane &&
				(basicTestOptions as OilTestingOptions).residualSolvent.butane
					? "(+Propane, +Butane)"
					: (basicTestOptions as OilTestingOptions).residualSolvent.propane
					? "(+Propane)"
					: (basicTestOptions as OilTestingOptions).residualSolvent.butane
					? "(+Butane)"
					: "",
		});
	}

	const filteredBasicOptions: Array<any> = basicTestOptionFields.filter(
		(option) => {
			return option.checked;
		}
	);
	return filteredBasicOptions;
}

export function convertSelectedMicroTestOptionsToFields(
	microTestOptions: MicroTestingOptions | undefined,
	translation: TFunction<"translation", undefined>
): Array<TestOptionFields> {
	const microTestOptionFields: Array<any> = [
		{
			id: "TAMC",
			title: translation("TAMC"),
			checked: microTestOptions?.TAMC,
		},
		{
			id: "TYMC",
			title: translation("TYMC"),
			checked: microTestOptions?.TYMC,
		},
		{
			id: "BTGN",
			title: translation("BTGN"),
			checked: microTestOptions?.BTGN.enabled,
			additionalInfo: microTestOptions?.BTGN.option,
		},
		{
			id: "Salmonella",
			title: translation("Salmonella"),
			checked: microTestOptions?.Salmonella.enabled,
			additionalInfo: microTestOptions?.Salmonella.option,
		},
		{
			id: "EColi",
			title: translation("EColi"),
			checked: microTestOptions?.EColi.enabled,
			additionalInfo: microTestOptions?.EColi.option,
		},
		{
			id: "Staphylococcus",
			title: translation("Staphylococcus"),
			checked: microTestOptions?.Staphylococcus.enabled,
			additionalInfo: microTestOptions?.Staphylococcus.option,
		},
		{
			id: "Pseudomonas",
			title: translation("Pseudomonas"),
			checked: microTestOptions?.Pseudomonas,
		},
		{
			id: "Viroids",
			title: translation("Viroids"),
			checked: microTestOptions?.viroids,
		},
		{
			id: "SexGenderTesting",
			title: translation("SexGenderTesting"),
			checked: microTestOptions?.sexGenderTesting,
		},
	];

	const filteredMicroOptions: Array<any> = microTestOptionFields.filter(
		(option) => {
			return option.checked;
		}
	);
	return filteredMicroOptions;
}

export function calculateMinimumWeight(
	subMatrix: SampleSubMatrix,
	basicTestOptions:
		| FlowerTestingOptions
		| OilTestingOptions
		| CombinedTestingOptions,
	microTestOptions: MicroTestingOptions
): number {
	const category: SampleCategory = getCategoryFromSubMatrix(subMatrix);
	if (category === SampleCategory.OTHER) {
		//no restriction
		return 0;
	}
	let minWeight: number = 0;
	const microTestEnabled: boolean =
		isAnyMicroTestWithoutAdditionalEnabled(microTestOptions);

	let isForeignMatterEnabled: boolean = false;
	//basic tests
	let basicTestCount: number = 0;
	for (const key of Object.keys(basicTestOptions) as Array<
		keyof (FlowerTestingOptions | OilTestingOptions | CombinedTestingOptions)
	>) {
		if (basicTestOptions[key] == true) {
			if (key === "foreignMatter") {
				isForeignMatterEnabled = true;
				continue;
			} else {
				basicTestCount++;
			}
		}
	}
	if (!microTestEnabled) {
		//current logic: first 2 needs 5 each, then 5 more for every 2 test, round down
		// For the first 2 counts, add 5 each
		if (basicTestCount === 1) {
			minWeight += 5; // If only one count, add 5
		} else if (basicTestCount >= 2) {
			minWeight += 2 * 5;
			basicTestCount -= 2; // Reduce the count by 2
		}
		// For every 2 counts after the first 2, add 5 more to total
		minWeight += Math.floor(basicTestCount / 2) * 5;
	} else {
		if (basicTestCount >= 1) {
			minWeight += 5; // If only one count, add 5
		}
		minWeight += Math.floor(basicTestCount / 5) * 5;
	}

	//micro test
	let microWeight: number = 0;
	if (microTestEnabled) {
		if (
			category === SampleCategory.FLOWER ||
			category === SampleCategory.HYBRID
		) {
			if (
				microTestOptions.Salmonella.enabled &&
				microTestOptions.Salmonella.option === SalmonCustomOption.TenGram
			) {
				microWeight = 25;
			} else {
				microWeight = 35;
			}
		} else microWeight = 25;
	}
	if (microTestOptions.viroids) {
		microWeight += 5;
	}
	if (microTestOptions.sexGenderTesting) {
		microWeight += 5;
	}
	minWeight += microWeight;

	if (isForeignMatterEnabled && minWeight < 25) {
		minWeight = 25;
	}

	return minWeight;
}

export function getInitialTestSettings(
	SAMPLE_PACKAGES: Map<TestPackages, TestPackageInfo>,
	category: SampleCategory,
	initialSampleTestInfos: Array<FrontendSampleTestInformation>
): [
	FlowerTestingOptions | OilTestingOptions | CombinedTestingOptions,
	MicroTestingOptions,
	boolean,
	string,
	FrontendTurnaroundTimeOptions,
	BasicTestOptionTimes,
	TurnaroundTime,
	TurnaroundTime | null
] {
	let testPackage: TestPackages;
	switch (category) {
		case SampleCategory.FLOWER:
			testPackage = TestPackages.CUSTOM_FLOWER;
			break;
		case SampleCategory.OIL:
			testPackage = TestPackages.CUSTOM_OIL;
			break;
		case SampleCategory.HYBRID:
		case SampleCategory.OTHER:
			testPackage = TestPackages.CUSTOM_HYBRID;
	}

	let basicTestSettings:
		| FlowerTestingOptions
		| OilTestingOptions
		| CombinedTestingOptions = (
		SAMPLE_PACKAGES.get(testPackage) as TestPackageInfo
	).defaultTestingOptions;
	let microTestSettings: MicroTestingOptions = (
		SAMPLE_PACKAGES.get(testPackage) as TestPackageInfo
	).defaultMicroTestingOptions;

	let additionalTest: boolean = false;
	let additionalTestDescription: string = "";
	let TATOption: FrontendTurnaroundTimeOptions | undefined = undefined;

	let basicTime: BasicTestOptionTimes = {
		cannabinoid: null,
		terpene: null,
		pesticides: null,
		mycotoxins: null,
		heavyMetals: null,
		foreignMatter: null,
		moistureContent: null,
		residualSolvent: null,
		waterActivity: null,
	};
	let microTime: TurnaroundTime = TurnaroundTime.STANDARD;
	let additionalTestTime: TurnaroundTime | null = null;
	if (!initialSampleTestInfos || initialSampleTestInfos.length === 0) {
		return [
			basicTestSettings,
			microTestSettings,
			additionalTest,
			additionalTestDescription,
			FrontendTurnaroundTimeOptions.STANDARD,
			basicTime,
			microTime,
			null,
		];
	} else {
		initialSampleTestInfos.forEach((sample) => {
			if (category === SampleCategory.FLOWER) {
				basicTestSettings = {
					cannabinoid:
						basicTestSettings.cannabinoid ||
						sample.basicTestOptions?.cannabinoid ||
						false,
					terpene:
						basicTestSettings.terpene ||
						sample.basicTestOptions?.terpene ||
						false,
					pesticides:
						basicTestSettings.pesticides ||
						sample.basicTestOptions?.pesticides ||
						false,
					mycotoxins:
						basicTestSettings.mycotoxins ||
						sample.basicTestOptions?.mycotoxins ||
						false,
					heavyMetals:
						basicTestSettings.heavyMetals ||
						sample.basicTestOptions?.heavyMetals ||
						false,
					foreignMatter:
						basicTestSettings.foreignMatter ||
						sample.basicTestOptions?.foreignMatter ||
						false,
					waterActivity:
						basicTestSettings.waterActivity ||
						sample.basicTestOptions?.waterActivity ||
						false,
					moistureContent:
						(basicTestSettings as FlowerTestingOptions).moistureContent ||
						(sample.basicTestOptions as FlowerTestingOptions)
							?.moistureContent ||
						false,
				};
			} else if (category === SampleCategory.OIL) {
				basicTestSettings = {
					cannabinoid:
						basicTestSettings.cannabinoid ||
						sample.basicTestOptions?.cannabinoid ||
						false,
					terpene:
						basicTestSettings.terpene ||
						sample.basicTestOptions?.terpene ||
						false,
					pesticides:
						basicTestSettings.pesticides ||
						sample.basicTestOptions?.pesticides ||
						false,
					mycotoxins:
						basicTestSettings.mycotoxins ||
						sample.basicTestOptions?.mycotoxins ||
						false,
					heavyMetals:
						basicTestSettings.heavyMetals ||
						sample.basicTestOptions?.heavyMetals ||
						false,
					foreignMatter:
						basicTestSettings.foreignMatter ||
						sample.basicTestOptions?.foreignMatter ||
						false,
					waterActivity:
						basicTestSettings.waterActivity ||
						sample.basicTestOptions?.waterActivity ||
						false,

					residualSolvent:
						(sample.basicTestOptions as OilTestingOptions).residualSolvent &&
						(sample.basicTestOptions as OilTestingOptions).residualSolvent
							.enabled
							? (sample.basicTestOptions as OilTestingOptions).residualSolvent
							: (basicTestSettings as OilTestingOptions).residualSolvent,
				};
			} else {
				basicTestSettings = {
					cannabinoid:
						basicTestSettings.cannabinoid ||
						sample.basicTestOptions?.cannabinoid ||
						false,
					terpene:
						basicTestSettings.terpene ||
						sample.basicTestOptions?.terpene ||
						false,
					pesticides:
						basicTestSettings.pesticides ||
						sample.basicTestOptions?.pesticides ||
						false,
					mycotoxins:
						basicTestSettings.mycotoxins ||
						sample.basicTestOptions?.mycotoxins ||
						false,
					heavyMetals:
						basicTestSettings.heavyMetals ||
						sample.basicTestOptions?.heavyMetals ||
						false,
					foreignMatter:
						basicTestSettings.foreignMatter ||
						sample.basicTestOptions?.foreignMatter ||
						false,
					waterActivity:
						basicTestSettings.waterActivity ||
						sample.basicTestOptions?.waterActivity ||
						false,
					moistureContent:
						(basicTestSettings as CombinedTestingOptions).moistureContent ||
						(sample.basicTestOptions as CombinedTestingOptions)
							?.moistureContent ||
						false,
					residualSolvent:
						(sample.basicTestOptions as CombinedTestingOptions)
							.residualSolvent &&
						(sample.basicTestOptions as CombinedTestingOptions).residualSolvent
							.enabled
							? (sample.basicTestOptions as CombinedTestingOptions)
									.residualSolvent
							: (basicTestSettings as CombinedTestingOptions).residualSolvent,
				};
			}
			basicTime = {
				cannabinoid: sample.basicTestOptions?.cannabinoid
					? sample.turnaroundTime
					: basicTime.cannabinoid,
				terpene: sample.basicTestOptions?.terpene
					? sample.turnaroundTime
					: basicTime.terpene,
				pesticides: sample.basicTestOptions?.pesticides
					? sample.turnaroundTime
					: basicTime.pesticides,
				mycotoxins: sample.basicTestOptions?.mycotoxins
					? sample.turnaroundTime
					: basicTime.mycotoxins,
				heavyMetals: sample.basicTestOptions?.heavyMetals
					? sample.turnaroundTime
					: basicTime.heavyMetals,
				foreignMatter: sample.basicTestOptions?.foreignMatter
					? sample.turnaroundTime
					: basicTime.foreignMatter,
				waterActivity: sample.basicTestOptions?.waterActivity
					? sample.turnaroundTime
					: basicTime.waterActivity,
				moistureContent: (sample.basicTestOptions as any)?.moistureContent
					? sample.turnaroundTime
					: basicTime.moistureContent,
				residualSolvent: (sample.basicTestOptions as any)?.residualSolvent
					? (sample.basicTestOptions as any)?.residualSolvent.enabled &&
					  sample.turnaroundTime
					: basicTime.residualSolvent,
			};
			microTestSettings = {
				TAMC: microTestSettings.TAMC || sample.microTestOptions?.TAMC || false,
				TYMC: microTestSettings.TYMC || sample.microTestOptions?.TYMC || false,
				BTGN:
					sample.microTestOptions?.BTGN && sample.microTestOptions?.BTGN.enabled
						? sample.microTestOptions?.BTGN
						: microTestSettings.BTGN,
				Salmonella:
					sample.microTestOptions?.Salmonella &&
					sample.microTestOptions?.Salmonella.enabled
						? sample.microTestOptions?.Salmonella
						: microTestSettings.Salmonella,
				EColi:
					sample.microTestOptions?.EColi &&
					sample.microTestOptions?.EColi.enabled
						? sample.microTestOptions?.EColi
						: microTestSettings.EColi,
				Staphylococcus:
					sample.microTestOptions?.Staphylococcus &&
					sample.microTestOptions?.Staphylococcus.enabled
						? sample.microTestOptions?.Staphylococcus
						: microTestSettings.Staphylococcus,
				Pseudomonas:
					microTestSettings.Pseudomonas ||
					sample.microTestOptions?.Pseudomonas ||
					false,
				viroids:
					microTestSettings.viroids ||
					sample.microTestOptions?.viroids ||
					false,
				sexGenderTesting:
					microTestSettings.sexGenderTesting ||
					sample.microTestOptions?.sexGenderTesting ||
					false,
			};

			microTime =
				sample.microTestOptions &&
				isAnyMicroTestEnabled(sample.microTestOptions)
					? sample.turnaroundTime
					: microTime;
			additionalTest = additionalTest || sample.additionalTest;
			additionalTestDescription = sample.additionalTest
				? sample.additionalTestDescription
				: additionalTestDescription;

			if (sample.additionalTest) {
				additionalTestTime = sample.turnaroundTime;
			}

			const sampleTAT: FrontendTurnaroundTimeOptions =
				TurnaroundTimeToTATOption(sample.turnaroundTime);

			if (!TATOption) {
				TATOption = sampleTAT;
			} else if (TATOption !== sampleTAT) {
				TATOption = FrontendTurnaroundTimeOptions.CUSTOM;
			} else {
				//same TAT, no op
			}
		});
	}
	return [
		basicTestSettings,
		microTestSettings,
		additionalTest,
		additionalTestDescription,
		TATOption as unknown as FrontendTurnaroundTimeOptions,
		basicTime,
		microTime,
		additionalTestTime,
	];
}

export function getBasicAndMicroDefaultTimesFromOptions(
	basicTestOptions:
		| FlowerTestingOptions
		| OilTestingOptions
		| CombinedTestingOptions,
	microTestOptions: MicroTestingOptions,
	defaultTime: TurnaroundTime
): [BasicTestOptionTimes, TurnaroundTime] {
	const basicTime: BasicTestOptionTimes = {
		cannabinoid: basicTestOptions.cannabinoid ? defaultTime : null,
		terpene: basicTestOptions.terpene ? defaultTime : null,
		pesticides: basicTestOptions.pesticides ? defaultTime : null,
		mycotoxins: basicTestOptions.mycotoxins ? defaultTime : null,
		heavyMetals: basicTestOptions.heavyMetals ? defaultTime : null,
		foreignMatter: basicTestOptions.foreignMatter ? defaultTime : null,
		waterActivity: basicTestOptions.waterActivity ? defaultTime : null,
		moistureContent: (basicTestOptions as any)?.moistureContent
			? defaultTime
			: null,
		residualSolvent: (basicTestOptions as any)?.residualSolvent
			? (basicTestOptions as any)?.residualSolvent.enabled && defaultTime
			: null,
	};
	const microTime: TurnaroundTime = defaultTime;
	return [basicTime, microTime];
}

export function TurnaroundTimeToTATOption(
	turnaroundTime: TurnaroundTime
): FrontendTurnaroundTimeOptions {
	switch (turnaroundTime) {
		case TurnaroundTime.STANDARD:
			return FrontendTurnaroundTimeOptions.STANDARD;
		case TurnaroundTime.RUSH:
			return FrontendTurnaroundTimeOptions.RUSH;
		case TurnaroundTime.URGENT:
			return FrontendTurnaroundTimeOptions.URGENT;
	}
}
export function TATOptionToTurnaroundTime(
	TATOption: FrontendTurnaroundTimeOptions
): TurnaroundTime {
	switch (TATOption) {
		case FrontendTurnaroundTimeOptions.STANDARD:
			return TurnaroundTime.STANDARD;
		case FrontendTurnaroundTimeOptions.RUSH:
			return TurnaroundTime.RUSH;
		case FrontendTurnaroundTimeOptions.URGENT:
			return TurnaroundTime.URGENT;
		default:
			return TurnaroundTime.STANDARD;
	}
}

export function getBasicTestTranslationFromKey(
	key: keyof BasicTestOptionTimes,
	translation: TFunction<"translation", undefined>
): string {
	switch (key) {
		case "cannabinoid":
			return translation("CannabinoidPotency");
		case "terpene":
			return translation("Terpene");
		case "pesticides":
			return translation("Pesticides");
		case "mycotoxins":
			return translation("Mycotoxins");
		case "heavyMetals":
			return translation("HeavyMetal");
		case "foreignMatter":
			return translation("ForeignMatter");
		case "moistureContent":
			return translation("MoistureContent");
		case "residualSolvent":
			return translation("ResidualSolvent");
		case "waterActivity":
			return translation("WaterActivity");
	}
}

// export function getMicroTestTranslationFromKey(
// 	key: keyof MicroTestingOptionTimes,
// 	translation: TFunction<"translation", undefined>
// ): string {
// 	switch (key) {
// 		case "TAMC":
// 			return translation("TAMC");
// 		case "TYMC":
// 			return translation("TYMC");
// 		case "BTGN":
// 			return translation("BTGN");
// 		case "Salmonella":
// 			return translation("Salmonella");
// 		case "EColi":
// 			return translation("EColi");
// 		case "Staphylococcus":
// 			return translation("Staphylococcus");
// 		case "Pseudomonas":
// 			return translation("Pseudomonas");
// 		case "treatment":
// 			return translation("Treatment");
// 		case "viroids":
// 			return translation("Viroids");
// 		case "sexGenderTesting":
// 			return translation("SexGenderTesting");
// 	}
// }

export function getShortTurnaroundTime(
	time: TurnaroundTime,
	translation: TFunction<"translation", undefined>
): string {
	switch (time) {
		case TurnaroundTime.STANDARD:
			return translation("Standard");
		case TurnaroundTime.RUSH:
			return translation("Rush");
		case TurnaroundTime.URGENT:
			return translation("Urgent");
	}
}

export function isPackageCustomized(
	basicTestOptions:
		| FlowerTestingOptions
		| OilTestingOptions
		| CombinedTestingOptions,
	microTestOptions: MicroTestingOptions,
	packageTemplateInfo: TestPackageInfo
): boolean {
	for (const key in basicTestOptions) {
		if (
			basicTestOptions[key as keyof typeof basicTestOptions] !=
			packageTemplateInfo.defaultTestingOptions[
				key as keyof typeof packageTemplateInfo.defaultTestingOptions
			]
		) {
			return true; // Return false if any property is not equal
		}
	}

	for (const key in microTestOptions) {
		if (
			typeof microTestOptions[key as keyof MicroTestingOptions] === "object"
		) {
			// For nested objects, use a recursive check
			if (
				!areNestedObjectsEqual(
					microTestOptions[key as keyof MicroTestingOptions],
					packageTemplateInfo.defaultMicroTestingOptions[
						key as keyof MicroTestingOptions
					]
				)
			) {
				return true;
			}
		} else {
			// For primitive properties, check equality directly
			if (
				microTestOptions[key as keyof MicroTestingOptions] !=
				packageTemplateInfo.defaultMicroTestingOptions[
					key as keyof MicroTestingOptions
				]
			) {
				return true;
			}
		}
	}
	return false;
}

function areNestedObjectsEqual(objA: any, objB: any): boolean {
	if (objA === objB) return true; // Same reference or both null/undefined

	for (const key in objA) {
		if (objA[key] != objB[key]) {
			return false; // Return false if any property is not equal
		}
	}
	return true; // All nested properties are equal
}

export function sortByTurnaroundItems(
	items: FrontendSampleTestInformation[]
): FrontendSampleTestInformation[] {
	const order = {
		[TurnaroundTime.STANDARD]: 1,
		[TurnaroundTime.RUSH]: 2,
		[TurnaroundTime.URGENT]: 3,
	};

	return items.sort(
		(a, b) => order[a.turnaroundTime] - order[b.turnaroundTime]
	);
}
export function getTurnaroundTimeBackgroundColor(
	turnaroundTime: TurnaroundTime
): string {
	switch (turnaroundTime) {
		case TurnaroundTime.STANDARD:
			return SecondaryColor.SECONDARY_100;
		case TurnaroundTime.RUSH:
			return SecondaryColor.SECONDARY_200;
		case TurnaroundTime.URGENT:
			return SecondaryColor.SECONDARY_400;
	}
}

export function getMatrixTranslationText(
	matrix: SampleMatrix,
	translation: TFunction<"translation", undefined>
): string {
	switch (matrix) {
		case SampleMatrix.DRIED_CANNABIS:
			return translation("DRIED_CANNABIS");
		case SampleMatrix.HEMP:
			return translation("HEMP");

		case SampleMatrix.FRESH_CANNABIS:
			return translation("FRESH_CANNABIS");

		case SampleMatrix.CONSUMABLES:
			return translation("CONSUMABLES");

		case SampleMatrix.TOPICALS:
			return translation("TOPICALS");

		case SampleMatrix.CHEMICAL_CONCENTRATE_EXTRACT:
			return translation("CHEMICAL_CONCENTRATE_EXTRACT");

		case SampleMatrix.PHYSICAL_CONCENTRATE_EXTRACT:
			return translation("PHYSICAL_CONCENTRATE_EXTRACT");

		case SampleMatrix.ACCESSORIES:
			return translation("ACCESSORIES");

		case SampleMatrix.OTHER:
			return translation("OTHER");
	}
}

export function getProductTypeTranslationText(
	type: InstrumentType,
	translation: TFunction<"translation", undefined>,
	abbreviation?: boolean
): string {
	switch (type) {
		case InstrumentType.GAS_CHROMATOGRAPHY:
			return translation("GAS_CHROMATOGRAPHY");
		case InstrumentType.LIQUID_CHROMATOGRAPHY:
			return translation("LIQUID_CHROMATOGRAPHY");
		case InstrumentType.MASS_SPECTROMETRY:
			return translation("MASS_SPECTROMETRY");
		case InstrumentType.PARTS_ACCESSORIES:
			return translation("PARTS_ACCESSORIES");
		case InstrumentType.SPECTROSCOPY_ELEMENTAL_ANALYSIS:
			return translation("SPECTROSCOPY_ELEMENTAL_ANALYSIS");
		case InstrumentType.NOT_SPECIFIED:
			return translation("NOT_SPECIFIED");
	}
}

export function getProductSourceTranslationText(
	source: InstrumentSource,
	translation: TFunction<"translation", undefined>
): string {
	switch (source) {
		case InstrumentSource.ORIGINAL_MANUFACTURER:
			return translation("ORIGINAL_MANUFACTURER");
		case InstrumentSource.OTHERS:
			return translation("OTHERS");
		case InstrumentSource.SPECTRALAB_SCIENTIFIC:
			return translation("SPECTRALAB_SCIENTIFIC");
	}
}

export function getProductConditionTranslationText(
	productCondition: InstrumentCondition,
	translation: TFunction<"translation", undefined>
): string {
	switch (productCondition) {
		case InstrumentCondition.LIKE_NEW:
			return translation("LIKE_NEW");
		case InstrumentCondition.TESTED_WORKING:
			return translation("TESTED_WORKING");
		case InstrumentCondition.USED:
			return translation("USED");
		case InstrumentCondition.REFURBISHED:
			return translation("REFURBISHED");
		case InstrumentCondition.AS_IS_CONDITION:
			return translation("AS_IS_CONDITION");
	}
}
export function getInstallFamiliarizeTranslationText(
	option: InstallFamiliarizeOptions,
	translation: TFunction<"translation", undefined>
): string {
	switch (option) {
		case InstallFamiliarizeOptions.ON_SITE:
			return translation("OnSite");
		case InstallFamiliarizeOptions.REMOTE:
			return translation("Remote");
	}
}

export function getRepairOptionTranslationText(
	option: RepairOptions,
	translation: TFunction<"translation", undefined>
): string {
	switch (option) {
		case RepairOptions.REPAIRS:
			return translation("Repairs");
		case RepairOptions.REPAIRACCESSMENT:
			return translation("RepairAccessment");
	}
}

export function getIQSelectionAbbreviationText(
	iqSelection: IQSelection,
	translation: TFunction<"translation", undefined>
): string {
	const abbreviations: string[] = [];

	if (iqSelection.preventativeMaintenance)
		abbreviations.push(translation("PreventativeMaintenanceAbb"));
	if (iqSelection.installationQualification)
		abbreviations.push(translation("InstallationQualificationAbb"));
	if (iqSelection.operationalQualification)
		abbreviations.push(translation("OperationalQualificationAbb"));
	if (iqSelection.performanceQualification)
		abbreviations.push(translation("PerformanceQualificationAbb"));

	return abbreviations.join("/");
}

// export function convertIQOptionsToFields(
// 	IQOptions: IQOptions,
// 	translation: TFunction<"translation", undefined>
// ): Array<TestOptionFields> {
// 	const IQOptionFields: Array<any> = [
// 		{
// 			id: "PreventativeMaintenance",
// 			title: translation("PreventativeMaintenance"),
// 			checked: IQOptions.preventativeMaintenance,
// 		},
// 		{
// 			id: "InstallationQualification",
// 			title: translation("InstallationQualification"),
// 			checked: IQOptions.installationQualification,
// 		},
// 		{
// 			id: "OperationalQualification",
// 			title: translation("OperationalQualification"),
// 			checked: IQOptions.operationalQualification,
// 		},
// 		{
// 			id: "PerformanceQualification",
// 			title: translation("PerformanceQualification"),
// 			checked: IQOptions.performanceQualification,
// 		},
// 	];

// 	const filteredIQOptions: Array<any> = IQOptionFields.filter((option) => {
// 		return option.checked;
// 	});
// 	return filteredIQOptions;
// }

export function getTaxOptionTranslationText(
	option: TaxOptions,
	translation: TFunction<"translation", undefined>
): string {
	switch (option) {
		case TaxOptions.ZERO:
			return translation("ZERO");
		case TaxOptions.EXCLUDED:
			return translation("EXCLUDED");
		case TaxOptions.OTHER:
			return translation("OTHER");
	}
}

export function getSubMatrixTranslationText(
	subMatrix: SampleSubMatrix,
	translation: TFunction<"translation", undefined>
): string {
	switch (subMatrix) {
		case DriedCannabisSubMatrix.FLOWER:
			return translation("Flower");
		case DriedCannabisSubMatrix.INFUSED_FLOWER:
			return translation("InfusedFlower");

		case DriedCannabisSubMatrix.MILLED:
			return translation("Milled");

		case DriedCannabisSubMatrix.INFUSED_MILLED:
			return translation("InfusedMilled");

		case DriedCannabisSubMatrix.PRE_ROLL:
			return translation("PreRoll");

		case DriedCannabisSubMatrix.INFUSED_PRE_ROLL:
			return translation("InfusedPreRoll");

		case DriedCannabisSubMatrix.SHAKE:
			return translation("Shake");

		case DriedCannabisSubMatrix.TRIM:
			return translation("Trim");

		case HempSubMatrix.HEMP:
			return translation("Hemp");

		case FreshCannabisSubMatrix.FLOWER:
			return translation("Flower");

		case FreshCannabisSubMatrix.LEAF:
			return translation("Leaf");

		case FreshCannabisSubMatrix.ROOT:
			return translation("Root");

		case ConsumablesSubMatrix.CANNABIS_OIL:
			return translation("CannabisOil");

		case ConsumablesSubMatrix.SPRAYS:
			return translation("Sprays");

		case ConsumablesSubMatrix.TINCTURES:
			return translation("Tinctures");

		case ConsumablesSubMatrix.BEVERAGE:
			return translation("Beverage");

		case ConsumablesSubMatrix.EDIBLES:
			return translation("Edibles");

		case TopicalsSubMatrix.CREAMS:
			return translation("Creams");

		case TopicalsSubMatrix.LINIMENTS:
			return translation("Liniments");

		case TopicalsSubMatrix.SALVES:
			return translation("Salves");

		case ChemicalConcentrateExtractSubMatrix.BADDER:
			return translation("Badder");

		case ChemicalConcentrateExtractSubMatrix.LIVE_BADDER:
			return translation("LiveBadder");

		case ChemicalConcentrateExtractSubMatrix.DIAMONDS:
			return translation("Diamonds");

		case ChemicalConcentrateExtractSubMatrix.DISTILLATE:
			return translation("Distillate");

		case ChemicalConcentrateExtractSubMatrix.HASH_OIL:
			return translation("HashOil");

		case ChemicalConcentrateExtractSubMatrix.ISOLATE:
			return translation("Isolate");

		case ChemicalConcentrateExtractSubMatrix.RESIN:
			return translation("Resin");

		case ChemicalConcentrateExtractSubMatrix.LIVE_RESIN:
			return translation("LiveResin");

		case ChemicalConcentrateExtractSubMatrix.SHATTER:
			return translation("Shatter");

		case ChemicalConcentrateExtractSubMatrix.WAX:
			return translation("Wax");

		case PhysicalConcentrateExtractSubMatrix.HASH:
			return translation("Hash");

		case PhysicalConcentrateExtractSubMatrix.DRY_SIFT_HASH:
			return translation("DrySiftHash");

		case PhysicalConcentrateExtractSubMatrix.KIEF:
			return translation("Kief");

		case PhysicalConcentrateExtractSubMatrix.PRESSED_KIEF:
			return translation("PressedKief");

		case PhysicalConcentrateExtractSubMatrix.ROSIN:
			return translation("Rosin");

		case PhysicalConcentrateExtractSubMatrix.FLOWER_ROSIN:
			return translation("FlowerRosin");

		case PhysicalConcentrateExtractSubMatrix.HASH_ROSIN:
			return translation("HashRosin");

		case PhysicalConcentrateExtractSubMatrix.LIVE_ROSIN:
			return translation("LiveRosin");

		case AccessoriesSubMatrix.ROLLING_PAPER:
			return translation("RollingPaper");

		case OtherSubMatrix.SOLID:
			return translation("OtherSolid");

		case OtherSubMatrix.INHALABLE:
			return translation("OtherInhalable");
	}
}

export function getItemTagTextColor(
	item: FrontendOrderItem,
	translation: TFunction<"translation", undefined>
): [text: string, textColor: string, backGroundColor: string] {
	const [text, textColor, backGroundColor] =
		item.itemType === OrderItemType.INSTRUMENT
			? item.service
				? [
						translation("InstrumentAndService"),
						NeutralColor.NEUTRAL_GREY_900,
						AccentColor.GREEN,
				  ]
				: [translation("Instrument"), "white", SecondaryColor.SECONDARY_900]
			: [
					translation("ServiceOnly"),
					NeutralColor.NEUTRAL_GREY_900,
					AccentColor.ORANGE,
			  ];
	return [text, textColor, backGroundColor];
}

// export function getOrderDetailItemTagTextColor(
// 	item: FrontendOrderItem,
// 	translation: TFunction<"translation", undefined>
// ): [text: string, textColor: string, backGroundColor: string] {
// 	const [text, textColor, backGroundColor] =
// 		item.itemType === OrderItemType.INSTRUMENT
// 			? item.service
// 				? [
// 						translation("InstrumentAndService"),
// 						NeutralColor.NEUTRAL_GREY_900,
// 						AccentColor.GREEN,
// 				  ]
// 				: [translation("Instrument"), "white", SecondaryColor.SECONDARY_900]
// 			: [
// 					translation("ServiceOnly"),
// 					NeutralColor.NEUTRAL_GREY_900,
// 					AccentColor.ORANGE,
// 			  ];
// 	return [text, textColor, backGroundColor];
// }

export function calculateItemPrice(item: FrontendOrderItem): number {
	const productTotal = item.product.unitPrice * item.product.quantity;
	const serviceTotal = item.service
		? item.service?.unitPrice * item.service?.quantity
		: 0;

	const total =
		item.itemType === OrderItemType.INSTRUMENT
			? productTotal + serviceTotal
			: serviceTotal;

	const roundedTotal = Math.round(total * 100) / 100;
	return roundedTotal; // Ensures 2 decimal places
}

export function calculatePrice(unitPrice: number, quantity: number): number {
	const total = unitPrice * quantity;

	const roundedTotal = Math.round(total * 100) / 100;
	return roundedTotal; // Ensures 2 decimal places
}

export function calculateDiscount(
	amount: number, // original price
	discount: number,
	discountUnit: DiscountUnit
): number {
	switch (discountUnit) {
		case DiscountUnit.NUMBER:
			return discount;
		case DiscountUnit.PERCENTAGE:
			return Math.round((amount * discount) / 100);
		default:
			return 0;
	}
}

export function formatPrice(price: number, currency: string) {
	return `${new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: currency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(price)}`;
}

export function getServiceAmount(item: FrontendOrderItem): number {
	if (!item.service) return 0;
	const serviceList = [
		item.service.installFamiliarize.enabled,
		item.service.instrumentQualification.enabled,
		item.service.repair.enabled,
		item.service.serviceContracts.enabled,
		Boolean(
			item.service.otherDescription && item.service.otherDescription?.length > 0
		),
	];
	return serviceList.filter(Boolean).length;
}

export function getServiceName(
	service: FrontendService,
	translation: TFunction<"translation", undefined>
): string {
	const serviceStr = [];
	if (service.installFamiliarize.enabled && service.installFamiliarize.option) {
		serviceStr.push(
			`${getInstallFamiliarizeTranslationText(
				service.installFamiliarize.option,
				translation
			)} ${translation("InstallationAndFamiliarization")}`
		);
	}
	if (service.instrumentQualification.enabled) {
		serviceStr.push(
			`${getIQSelectionAbbreviationText(
				service.instrumentQualification,
				translation
			)}`
		);
	}
	if (service.repair.enabled && service.repair.option) {
		serviceStr.push(
			`${getRepairOptionTranslationText(service.repair.option, translation)}`
		);
	}
	if (service.serviceContracts.enabled && service.serviceContracts.time) {
		serviceStr.push(
			`${service.serviceContracts.time}-${translation("month")} ${translation(
				"ServiceContracts"
			)}}`
		);
	}

	return serviceStr.join(", ");
}

export function extractInternalCodeFromFileName(
	fileName: string
): string | null {
	const regex = /--(\d+_\d+)-/;
	const match = fileName.match(regex);
	return match ? match[1] : null;
}
